"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAttachNewImages = exports.useConvertDataURLToFile = void 0;
const react_1 = require("react");
const api_1 = __importDefault(require("../../lib/api"));
const InputHtml_hooks_1 = require("./InputHtml.hooks");
const useFilterImageAttachments_1 = require("./useFilterImageAttachments");
const useProxyUrl_1 = require("./useProxyUrl");
const BASE64_MARKER = ';base64,';
// NOTE: taken from https://gist.github.com/kylefox/c09a7b738aacf088c2ec2b359ddcc5ea
function useConvertDataURLToFile() {
    // check if given URL looks like a base64-encoded URL
    const isDataURI = (0, react_1.useCallback)((url) => {
        return url.split(BASE64_MARKER).length === 2;
    }, []);
    const dataURItoFile = (0, react_1.useCallback)((dataURI, getFileName) => {
        if (!isDataURI(dataURI)) {
            return;
        }
        const [mimePart = '', contentPart = ''] = dataURI.split(BASE64_MARKER);
        // Format of a base64-encoded URL:
        // data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAYAAAAEOCAIAAAAPH1dAAAAK
        const mime = mimePart.split(':')[1];
        const filename = (getFileName === null || getFileName === void 0 ? void 0 : getFileName(mime)) ||
            'dataURI-file-' + new Date().getTime() + '.' + mime.split('/')[1];
        const bytes = atob(contentPart);
        const writer = new Uint8Array(new ArrayBuffer(bytes.length));
        for (let i = 0; i < bytes.length; i++) {
            writer[i] = bytes.charCodeAt(i);
        }
        return new File([writer.buffer], filename, { type: mime });
    }, [isDataURI]);
    return dataURItoFile;
}
exports.useConvertDataURLToFile = useConvertDataURLToFile;
function useAttachNewImages(props) {
    const { uploader, attachments, proxyOptions, onCompleteUpload, onChangeAttachments } = props;
    const onChangeAttachmentsRef = (0, react_1.useRef)(onChangeAttachments);
    onChangeAttachmentsRef.current = onChangeAttachments;
    const onCompleteUploadRef = (0, react_1.useRef)(onCompleteUpload);
    onCompleteUploadRef.current = onCompleteUpload;
    // get all src from value
    // get storageKeys from src
    // check if storageKey exists in attachments
    // if storageKey exists but does not exist for attachments, reupload the image
    // if no storageKey, upload the image src
    const uploadedBlacklist = (0, react_1.useRef)([]);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const convertToFile = useConvertDataURLToFile();
    const imageAttachments = (0, useFilterImageAttachments_1.useFilterImageAttachments)(attachments);
    const getStorageKeyFromURL = (0, react_1.useCallback)((url) => {
        // if uploaded, get storageKey
        if ((0, InputHtml_hooks_1.isProxyURL)(url, proxyOptions)) {
            // assume url has converted &amp; to &
            const paramsStr = url.split('?')[1] || '';
            const params = new URLSearchParams(paramsStr);
            const storageKey = params.get(proxyOptions.key);
            if (storageKey) {
                return storageKey;
            }
        }
    }, [proxyOptions]);
    const getURLData = (0, react_1.useCallback)((urls) => {
        return urls.map(url => {
            // convert &amp; to &
            const ampUrl = url.replace(/&amp;/g, '&');
            const storageKey = getStorageKeyFromURL(ampUrl);
            return { url: ampUrl, originalUrl: url, storageKey };
        });
    }, [getStorageKeyFromURL]);
    const getExtensionFromContentType = (0, react_1.useCallback)((contentType) => {
        const extension = (contentType.split('/')[1] || '').trim();
        return extension ? `.${extension}` : '';
    }, []);
    const getExistingStorageKeys = (0, react_1.useCallback)(() => {
        // get existing storageKeys from imageAttachments
        const existingKeys = [];
        for (const attachment of imageAttachments) {
            if (attachment.url) {
                existingKeys.push(attachment.url);
            }
        }
        // include uploaded list to avoid duplicate upload
        existingKeys.push(...uploadedBlacklist.current);
        return Array.from(new Set(existingKeys));
    }, [imageAttachments]);
    const filterUploadData = (0, react_1.useCallback)((urlData) => {
        const existingStorageKeys = getExistingStorageKeys();
        return urlData.filter(urlData => {
            // upload if no storageKey, or if not in existing storageKeys
            return (!urlData.storageKey ||
                !existingStorageKeys.includes(urlData.storageKey));
        });
    }, [getExistingStorageKeys]);
    const createFileName = (0, react_1.useCallback)((mimeType, index) => {
        const extension = getExtensionFromContentType(mimeType) || '.png';
        const timestamp = +new Date();
        const fileName = `IMG_${timestamp}-${index}${extension}`;
        return { fileName, extension, timestamp };
    }, [getExtensionFromContentType]);
    const convertURLDataToFile = (0, react_1.useCallback)((urlData, index) => __awaiter(this, void 0, void 0, function* () {
        // fetch and upload url data
        if (urlData.url.startsWith('http')) {
            const response = yield fetch(urlData.url);
            // get extension and create fileName
            const contentType = response.headers.get('content-type') || 'image/png';
            const { fileName, timestamp } = createFileName(contentType, index);
            const blob = yield response.blob();
            return new File([blob], fileName, {
                type: contentType,
                lastModified: timestamp
            });
        }
        try {
            // is dataURL
            const file = convertToFile(urlData.url, (mimeType) => createFileName(mimeType, index).fileName);
            if (file) {
                return file;
            }
        }
        catch (_a) {
            // ignore error
        }
        throw new Error('Unable to display image. Consider uploading the image instead.');
    }), [createFileName, convertToFile]);
    const uploadImages = (0, react_1.useCallback)((urlData) => __awaiter(this, void 0, void 0, function* () {
        if (!uploader) {
            return [];
        }
        const files = yield Promise.all(urlData.map(convertURLDataToFile));
        // upload files
        setIsLoading(true);
        const uploadFiles = yield uploader.uploadAsync(files);
        setIsLoading(false);
        // map uploaded results
        const value = uploadFiles.map(item => {
            var _a, _b;
            return {
                url: (_a = item.presignedUrl) === null || _a === void 0 ? void 0 : _a.storageKey,
                signedUrl: (_b = item.presignedUrl) === null || _b === void 0 ? void 0 : _b.signedUrl,
                name: item.file.name,
                size: item.file.size
            };
        });
        return value.map((attachment, index) => {
            return { attachment, urlData: urlData[index] };
        });
    }), [uploader, convertURLDataToFile]);
    const inspectAndUpload = (0, react_1.useCallback)((value, removeToken = true) => __awaiter(this, void 0, void 0, function* () {
        var _b, _c;
        // ignore images already uploaded (within baseUrl)
        const baseUrl = api_1.default.getBaseUrl();
        const urls = (0, InputHtml_hooks_1.getSrcFromText)(value).filter(url => !url.includes(baseUrl));
        const uploadData = filterUploadData(getURLData(urls));
        if (uploadData.length === 0) {
            return value;
        }
        // upload images
        const items = yield uploadImages(uploadData);
        (_b = onChangeAttachmentsRef.current) === null || _b === void 0 ? void 0 : _b.call(onChangeAttachmentsRef, items.map(item => item.attachment));
        // replace each existing url
        for (const { attachment, urlData } of items) {
            if (!value.includes(urlData.originalUrl)) {
                continue;
            }
            const url = (0, useProxyUrl_1.createProxyUrl)(attachment.url || '', proxyOptions, undefined, true);
            removeToken && url.searchParams.delete('token');
            value = value.replace(urlData.originalUrl, url.toString());
        }
        (_c = onCompleteUploadRef.current) === null || _c === void 0 ? void 0 : _c.call(onCompleteUploadRef);
        return value;
    }), [proxyOptions, getURLData, uploadImages, filterUploadData]);
    return { inspectAndUpload, isLoading };
}
exports.useAttachNewImages = useAttachNewImages;
//# sourceMappingURL=useAttachNewImages.js.map